import React from 'react';
import { 
  Download, FileText, Mail, Printer, ExternalLink,
  CheckCircle, Users, Calendar, CreditCard, FileBox,
  Shield, BarChart3, MessageSquare, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Brochure() {
  const handleDownloadPDF = async () => {
    // For now, open the markdown in a new tab - in production this would generate a PDF
    window.open(`${API_URL}/api/docs/brochure`, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent('Scopri Assietica Gest - Il Gestionale per il Terzo Settore');
    const body = encodeURIComponent(`Ciao,

Ti segnalo Assietica Gest, il gestionale completo per associazioni e organizzazioni del Terzo Settore.

Scopri tutte le funzionalità su: https://www.assieticagest.it

Caratteristiche principali:
- Gestione Soci e Volontari
- Eventi e Presenze
- Rendicontazione Economica (120+ categorie)
- Documenti e Kit Amministrativo
- Quote Associative
- E molto altro!

Piano Free disponibile per iniziare subito.

Per maggiori informazioni: supporto@assietica.it
`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const features = [
    {
      icon: Users,
      title: 'Gestione Soci e Volontari',
      description: 'Anagrafica completa, classificazione Stabile/Occasionale, import massivo CSV/Excel con rollback, tessere automatiche'
    },
    {
      icon: Calendar,
      title: 'Eventi e Presenze',
      description: 'Calendario visuale, check-in/out rapido, calcolo ore automatico, report partecipazione'
    },
    {
      icon: CreditCard,
      title: 'Quote Associative',
      description: 'Gestione quote annuali, stati pagamento, eliminazione quote, contabilizzazione automatica, ricevute PDF'
    },
    {
      icon: BarChart3,
      title: 'Rendicontazione Economica',
      description: '120+ categorie predefinite, gestione progetti, bilancio automatico, export PDF/CSV'
    },
    {
      icon: FileBox,
      title: 'Gestione Documentale',
      description: 'Upload sicuro, organizzazione per categoria, scadenze, backup completo'
    },
    {
      icon: Shield,
      title: 'Coperture Assicurative',
      description: 'Registro coperture, alert scadenze, dashboard rischi, storico polizze'
    },
    {
      icon: FileText,
      title: 'Kit Amministrativo',
      description: '15+ documenti automatici: domande, verbali, ricevute, tessere, deleghe'
    },
    {
      icon: MessageSquare,
      title: 'Comunicazioni',
      description: 'Email via Gmail/Outlook a Volontari e Soci, WhatsApp, notifiche eventi. Non richiede configurazione SMTP.'
    },
    {
      icon: Shield,
      title: 'Sicurezza e Notifiche',
      description: 'Recupero password via email, notifiche automatiche nuove registrazioni, login Google, paginazione liste grandi'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="brochure-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#0055BB] to-[#003d88] text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Assietica Gest
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-2">
            Il Gestionale Completo per il Terzo Settore Italiano
          </p>
          <p className="text-lg opacity-80 mb-8">
            Semplifica. Organizza. Cresci.
          </p>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={handleDownloadPDF}
              className="bg-white text-[#0055BB] hover:bg-slate-100"
              size="lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Scarica Brochure PDF
            </Button>
            <Button 
              onClick={handleEmail}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              size="lg"
            >
              <Mail className="w-5 h-5 mr-2" />
              Invia via Email
            </Button>
            <Button 
              onClick={handlePrint}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              size="lg"
            >
              <Printer className="w-5 h-5 mr-2" />
              Stampa
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        
        {/* Intro */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            La soluzione digitale per Associazioni, ODV, APS e tutti gli Enti del Terzo Settore
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Sviluppato da <strong>Assietica Società Cooperativa – Società Benefit</strong>, 
            nasce dall'esperienza diretta nel mondo del volontariato e della gestione associativa.
          </p>
        </div>

        {/* Features Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-slate-800 mb-8 text-center">
            Tutto in Un'Unica Piattaforma
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-lg bg-[#0055BB]/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#0055BB]" />
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-2">{feature.title}</h4>
                  <p className="text-sm text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Plans - Compact Table */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">
            Piani e Prezzi
          </h3>
          <p className="text-center text-slate-600 mb-6 text-sm">
            Ogni ente ha diritto a <strong>1 utente</strong>. Limiti basati su volontari attivi e soggetti unici.
          </p>
          
          {/* Compact Table for Print */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0055BB] text-white">
                  <th className="border border-slate-300 p-2 text-left">Piano</th>
                  <th className="border border-slate-300 p-2 text-center">Prezzo/mese</th>
                  <th className="border border-slate-300 p-2 text-center">Volontari</th>
                  <th className="border border-slate-300 p-2 text-center">Soggetti</th>
                  <th className="border border-slate-300 p-2 text-center">Eventi/anno</th>
                  <th className="border border-slate-300 p-2 text-center">Magazzino e Automezzi</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  <td className="border border-slate-300 p-2 font-semibold">FREE</td>
                  <td className="border border-slate-300 p-2 text-center">Gratuito</td>
                  <td className="border border-slate-300 p-2 text-center">20</td>
                  <td className="border border-slate-300 p-2 text-center">30</td>
                  <td className="border border-slate-300 p-2 text-center">10</td>
                  <td className="border border-slate-300 p-2 text-center">-</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2 font-semibold text-blue-700">PREMIUM</td>
                  <td className="border border-slate-300 p-2 text-center">€9,99</td>
                  <td className="border border-slate-300 p-2 text-center">60</td>
                  <td className="border border-slate-300 p-2 text-center">100</td>
                  <td className="border border-slate-300 p-2 text-center">50</td>
                  <td className="border border-slate-300 p-2 text-center">-</td>
                </tr>
                <tr className="bg-indigo-50">
                  <td className="border border-slate-300 p-2 font-semibold text-indigo-700">GOVERNANCE</td>
                  <td className="border border-slate-300 p-2 text-center">€14,99</td>
                  <td className="border border-slate-300 p-2 text-center">100</td>
                  <td className="border border-slate-300 p-2 text-center">200</td>
                  <td className="border border-slate-300 p-2 text-center">100</td>
                  <td className="border border-slate-300 p-2 text-center">-</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="border border-slate-300 p-2 font-semibold text-amber-700">PRO</td>
                  <td className="border border-slate-300 p-2 text-center">€24,99</td>
                  <td className="border border-slate-300 p-2 text-center">250</td>
                  <td className="border border-slate-300 p-2 text-center">500</td>
                  <td className="border border-slate-300 p-2 text-center">Illimitati</td>
                  <td className="border border-slate-300 p-2 text-center font-semibold text-amber-700">Incluso</td>
                </tr>
                <tr className="bg-purple-50">
                  <td className="border border-slate-300 p-2 font-semibold text-purple-700">ENTERPRISE</td>
                  <td className="border border-slate-300 p-2 text-center">€49,99</td>
                  <td className="border border-slate-300 p-2 text-center">Illimitati</td>
                  <td className="border border-slate-300 p-2 text-center">Illimitati</td>
                  <td className="border border-slate-300 p-2 text-center">Illimitati</td>
                  <td className="border border-slate-300 p-2 text-center font-semibold text-purple-700">Incluso</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="border border-slate-300 p-2 font-semibold text-emerald-700">CONVENZIONI</td>
                  <td className="border border-slate-300 p-2 text-center">Su richiesta</td>
                  <td className="border border-slate-300 p-2 text-center">Illimitati</td>
                  <td className="border border-slate-300 p-2 text-center">Illimitati</td>
                  <td className="border border-slate-300 p-2 text-center">Illimitati</td>
                  <td className="border border-slate-300 p-2 text-center font-semibold text-emerald-700">Incluso</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center text-xs text-slate-500 mt-3">
            Prezzi IVA inclusa. Sconto 10% con abbonamento annuale.
          </p>
          
          {/* Funzionalità per piano */}
          <div className="mt-6 p-4 bg-slate-100 rounded-lg text-sm">
            <h4 className="font-semibold text-slate-700 mb-2">Funzionalità principali per piano:</h4>
            <ul className="space-y-1 text-slate-600">
              <li><strong className="text-slate-800">FREE:</strong> Gestione volontari e soci (max 20), eventi (max 10/anno), presenze, quote (visualizzazione), Dashboard base, Kit Amministrativo ETS</li>
              <li><strong className="text-blue-700">PREMIUM:</strong> + Comunicazioni Email/WhatsApp, Kit Soci completo, Archivio documenti, Certificati base, Report PDF, Export (RUNTS), Calendario Scadenzario</li>
              <li><strong className="text-indigo-700">GOVERNANCE:</strong> + Modulo Governance (Organi Sociali, Assemblee, Consiglio Direttivo), Rendicontazione economica (35 categorie), Certificati personalizzati, Libri Sociali PDF, Import Massivo (max 200), Schemi Ministeriali Bilancio (MOD. D, C)</li>
              <li><strong className="text-amber-700">PRO:</strong> + Rendicontazione completa (120+ categorie), Statistiche avanzate con grafici, Gestione Magazzino e Assegnazioni, Gestione Automezzi (assicurazioni, revisioni, manutenzioni con scadenzario), Import Massivo (max 500), Chatbot AI, Generazione batch certificati, Schemi Ministeriali completi (MOD. A, B)</li>
              <li><strong className="text-purple-700">ENTERPRISE:</strong> + Volontari e soggetti illimitati, Supporto prioritario via email (supporto@assietica.it)</li>
              <li><strong className="text-emerald-700">CONVENZIONI:</strong> + Gestione multi-ente, Dashboard federazione, Reportistica aggregata, Account manager dedicato, Formazione inclusa</li>
            </ul>
          </div>
          
          {/* Nota Comunicazioni */}
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <h4 className="font-semibold text-amber-800 mb-2">📧 Nota sul Modulo Comunicazioni</h4>
            <p className="text-amber-700">
              Il modulo Comunicazioni <strong>non invia email automaticamente</strong>. Permette di inviare messaggi a 
              <strong> Volontari e Soci</strong> tramite Gmail, Outlook o WhatsApp con i destinatari già precompilati. 
              Non è richiesta alcuna configurazione SMTP. Per istruzioni dettagliate, 
              consulta il <strong>Manuale d'Uso</strong> o chiedi all'<strong>Assistente AI</strong>.
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-slate-50 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">
            Perché Scegliere Assietica Gest?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-[#0055BB] mb-2">70%</div>
              <p className="text-slate-600">Tempo risparmiato nella gestione</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#0055BB] mb-2">120+</div>
              <p className="text-slate-600">Categorie contabili predefinite</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#0055BB] mb-2">15+</div>
              <p className="text-slate-600">Documenti generabili automaticamente</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#0055BB] mb-2">99.9%</div>
              <p className="text-slate-600">Uptime garantito</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-[#0055BB] to-[#003d88] text-white rounded-2xl p-12">
          <Award className="w-16 h-16 mx-auto mb-4 opacity-80" />
          <h3 className="text-3xl font-bold mb-4">
            Inizia Oggi - È Gratuito!
          </h3>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Nessun impegno. Nessuna carta di credito richiesta.
            Solo la semplicità di gestire al meglio la tua associazione.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              className="bg-white text-[#0055BB] hover:bg-slate-100"
              onClick={() => window.open('https://www.assieticagest.it/login', '_blank')}
            >
              Registrati Gratis
              <ExternalLink className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => window.location.href = 'mailto:supporto@assietica.it'}
            >
              Contattaci
              <Mail className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-slate-600">
          <p className="mb-2">
            <strong>Assietica Società Cooperativa – Società Benefit</strong>
          </p>
          <p>
            Email: <a href="mailto:supporto@assietica.it" className="text-[#0055BB]">supporto@assietica.it</a> | 
            Sito: <a href="https://www.assietica.it" target="_blank" rel="noopener noreferrer" className="text-[#0055BB]">www.assietica.it</a>
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
